<template>
  <div
    class="base-select"
    v-bind:class="{ 'is-multiple': isMultiple, 'is-small': isSmall, 'is-compact': isCompact }"
    @click="opensSheet && $emit('open')"
  >
    <FormLabel :is-required="isRequired">{{ label }}</FormLabel>
    <VSelect
      :items="items"
      :item-text="itemText"
      :item-value="itemValue"
      :menu-props="{
        contentClass: isMultiple ? 'filter-menu--multiple' : 'filter-menu--simple',
        offsetY: true,
        auto: menuAuto,
      }"
      :multiple="isMultiple"
      :placeholder="_placeholder"
      :rounded="false"
      @change="onChange"
      dense
      filled
      flat
      :hint="hint"
      persistent-hint
      :name="name"
      v-validate="isRequired && items.length ? 'required' : ''"
      v-model="values"
      :error-messages="errors.has(name) ? $t('form-select.errors') : ''"
      :disabled="!items.length || isDisabled"
      :readonly="opensSheet"
      attach
      v-on:click:append="toggleMenu"
      ref="searchFilter"
      :messages="messages"
    >
      <template v-if="isMultiple" v-slot:selection="{ item, index }">
        <span class="v-select__selection v-select__selection--comma" v-if="values.length > 1 && index === 0">
          {{ $t('form-select.other-selected', { length: values.length }) }}
        </span>
        <span class="v-select__selection v-select__selection--comma" v-else-if="index === 0">
          {{ explicitValues ? item : item.text }}
        </span>
      </template>

      <template v-else-if="document" v-slot:selection="{ item }">
        <BaseIcon v-if="documentIcon" class="base-select__icon" :name="getDocumentIcon(item)" />
        <span class="v-select__selection v-select__selection--comma">{{ item.name }}</span>
      </template>

      <!-- optional custom row in the dropdown; the row stays selectable -->
      <template v-if="$scopedSlots.item" v-slot:item="scope">
        <slot name="item" v-bind="scope" />
      </template>
    </VSelect>
  </div>
</template>

<script>
import FormLabel from '@/components/form/FormLabel';
import BaseIcon from '@/components/base/BaseIcon';
import { VSelect } from 'vuetify/lib';
import { getDocumentIcon } from '@/util/document-file-util';

export default {
  name: 'BaseSelect',
  components: {
    FormLabel,
    VSelect,
    BaseIcon,
  },
  inject: ['$validator'],
  props: {
    isMultiple: {
      type: Boolean,
      default: false,
    },
    isSmall: {
      type: Boolean,
      default: false,
    },
    isCompact: {
      type: Boolean,
      default: false,
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
    label: {
      type: String,
      default: '',
    },
    name: {
      type: String,
      default: '',
    },
    hint: {
      type: String,
      default: '',
      required: false,
    },
    // items should be an array of objects containing text/value properties
    // cf. https://vuetifyjs.com/en/components/selects
    items: {
      type: Array,
      required: true,
      default: () => [],
    },
    itemText: {
      type: String,
      required: false,
    },
    document: {
      type: Boolean,
      required: false,
    },
    documentIcon: {
      type: String,
      required: false,
    },
    itemValue: {
      type: String,
      required: false,
    },
    placeholder: {
      type: String,
      required: false,
      default: '',
    },
    value: {
      required: false,
    },
    menuAuto: {
      type: Boolean,
      default: false,
    },
    explicitValues: {
      type: Boolean,
      default: false,
    },
    isRequired: Boolean,
    messages: {
      type: [Array, String],
      default: '',
    },
    // The page shows the choices in a sheet (phones, docs/page-layout.md): the field opens no menu
    // and emits `open`.
    opensSheet: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      values: this.value,
    };
  },
  computed: {
    areAllSelected() {
      return this.isMultiple && this.values && this.values.length === this.items.length;
    },
    areSomeSelected() {
      return this.isMultiple && this.values && this.values.length > 0 && !this.areAllSelected;
    },
    icon() {
      if (this.areAllSelected) return 'mdi-checkbox-marked';
      if (this.areSomeSelected) return 'mdi-minus-box';
      return 'mdi-checkbox-blank-outline';
    },
    _placeholder() {
      if (this.placeholder.length) {
        return this.placeholder;
      }
      return !this.items.length ? 'N/A' : '';
    },
  },
  methods: {
    getDocumentIcon,
    onChange() {
      if (this.documentIcon) {
        const selectedDocument = this.items.find((item) => item[this.itemValue] === this.values);
        this.$emit(
          'change',
          selectedDocument ? { selectedFile: selectedDocument[this.itemText], documentId: this.values } : null,
        );
      } else if (this.explicitValues) {
        this.$emit('change', this.values, true);
      } else {
        this.$emit('change', this.areAllSelected ? [] : this.values, true);
      }
    },
    getName(item) {
      return item[this.itemText];
    },
    toggleMenu() {
      // The caret's click stops before the root's.
      if (this.opensSheet) return this.$emit('open');
      const element = this.$refs.searchFilter;
      if (element.isMenuActive) {
        this.$refs.searchFilter.isMenuActive = false;
        element.blur();
      } else {
        this.$refs.searchFilter.isMenuActive = true;
        element.focus();
      }
    },
  },
  watch: {
    value() {
      if (this.isMultiple) {
        // For multi-select: fall back to empty array, and treat an empty selection
        // as "select all" (the select-all toggle behaviour).
        this.values = this.value || [];
        if (this.values.length === 0) {
          this.values = this.items.map((item) => item.value);
        }
      } else {
        // For single-select: assign directly so falsy values (null, '') are kept
        // as-is and the matching option is displayed correctly.
        this.values = this.value;
      }
    },
  },
};
</script>

<style lang="scss">
.base-select {
  display: flex;
  flex: auto;
  flex-direction: column;

  &:not(.is-compact) {
    height: 90px;
  }

  &__icon {
    flex: 0 0 18px;
    height: 18px;
    width: 18px;
    margin-right: 8px;
    color: $color-normal-pale;
    &.selected {
      color: $color-accent;
    }
  }

  .error--text {
    color: $color-error;
  }
  .v-select__selections {
    color: $color-normal !important;
    flex-wrap: nowrap;
    padding-top: 0 !important;

    .v-select__selection {
      font-size: 14px;
      max-width: 100%;

      &.v-select__selection--comma {
        margin-right: 0 !important;
      }
    }
    .v-list-item__content {
      flex: unset;
      .v-list-item__title {
        width: 100%;
      }
    }
  }

  .v-input__append-inner {
    margin-top: 8px !important;
    padding: 0 !important;

    .v-icon {
      color: $color-normal;
      height: 5px;
      object-fit: contain;
      width: 10px;
    }
  }

  .v-input {
    &__slot {
      background: $color-neutral-pale !important;
      border: 1px solid $color-border !important;
      transition: background 200ms ease, border 200ms ease, box-shadow 200ms ease !important;
      border-radius: $radius-md !important;
      padding: 0 12px !important;

      &:hover {
        border-color: $color-normal-pale !important;
      }

      &::before {
        border: 0px !important;
      }

      &::after {
        border: 0px !important;
      }
    }
  }

  .v-select--is-menu-active {
    .v-input__slot {
      border-color: $color-accent !important;
      box-shadow: 0 0 0 3px rgba($color-accent-500, 0.1) !important;

      &::after {
        border: 0px !important;
      }
    }
  }

  &:not(.is-multiple) {
    .v-select__selections {
      .v-select__selection--comma {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }
  }

  // Small version
  &.is-small {
    height: auto;

    .form-label,
    .v-text-field__details {
      display: none;
    }

    .v-input__slot {
      min-height: 36px !important;
      max-width: 516px !important;
      margin-bottom: 0px;
      border-radius: $border-radius-large !important;
      padding: 0 8px 0 16px !important;
    }

    .v-input__append-inner {
      margin-top: 4px !important;
    }
  }

  &.is-compact {
    height: unset;
    padding: 4px;

    .v-input__slot {
      min-height: unset !important;
      margin-bottom: 0px;
      // padding: 4px !important;
    }

    .v-input__append-inner {
      margin-top: 4px !important;
    }

    .form-label,
    .v-text-field__details {
      display: none;
    }
  }
}

.filter-menu {
  &--simple {
    border-radius: $border-radius-small;
    box-shadow: $shadow-dropdown;
    text-overflow: ellipsis;

    .v-select-list {
      border-radius: inherit !important;
    }

    .v-list {
      .v-list-item {
        min-height: 30px;

        .v-list-item__content .v-list-item__title {
          color: $color-normal;
          font-size: $font-size-small;
        }

        &:hover {
          &::before {
            background-color: $color-accent;
          }

          .v-list-item__content .v-list-item__title {
            color: $color-normal;
          }
        }
      }

      .v-list-item--active {
        cursor: default;
        pointer-events: none;

        &::before {
          background-color: transparent;
        }

        .v-list-item__content .v-list-item__title {
          color: $color-accent;
        }
      }
    }
  }

  &--multiple {
    border-radius: $border-radius-small;
    box-shadow: $shadow-dropdown;

    .v-select-list {
      border-radius: inherit !important;
    }

    .base-select__prepend-item {
      color: $color-accent;
      font-size: $font-size-small;
      min-height: 30px;

      .separator {
        border-bottom: $grey-border;
        height: 1px;
        margin: 0 1rem;
        min-height: 1px !important;
        padding: 0;
      }

      .v-list-item:first-child {
        margin-bottom: 10px;

        .v-list-item__action {
          margin: 0;

          .v-input--selection-controls__ripple {
            left: -10px;
          }

          .v-icon {
            color: $color-accent !important;
            font-size: 28px;

            &.mdi-checkbox-blank-outline {
              color: $color-neutral !important;
            }
          }
        }

        .v-list-item__content {
          margin: 6px;

          .v-list-item__title.all {
            font-weight: bold;
          }
        }
      }

      & + .v-list-item {
        margin-top: 10px;
      }
    }

    .v-list-item {
      padding: 0px 8px;
      min-height: 30px;

      .v-list-item__content {
        margin-left: 8px;

        .v-list-item__title {
          color: $color-normal;
          font-size: $font-size-small;
          margin-right: 8px;
        }
      }

      &:hover {
        &::before {
          background-color: $color-accent;
        }

        .v-list-item__content .v-list-item__title {
          color: $color-normal;
        }
      }

      .v-list-item__action {
        margin: 0;

        .v-input--selection-controls__ripple {
          left: -10px;
        }

        .v-icon {
          color: $color-accent;
          font-size: 28px;

          &.mdi-checkbox-blank-outline {
            color: $color-neutral;
          }
        }
      }
    }

    .v-list-item--active {
      &::before {
        background-color: transparent;
      }

      .v-list-item__action {
        .v-icon {
          color: $color-accent;
        }
      }

      .v-list-item__content .v-list-item__title {
        color: $color-normal;
        font-weight: bold;
      }
    }
  }
}

.v-text-field__details {
  padding-left: 0px !important;

  .v-messages__message {
    color: $color-neutral;
    font-size: 12px;
  }
}
</style>
